import { Injectable } from '@angular/core';
import { ContextService } from '@core/services/core/context.service';
import * as moment from 'moment';
import { BehaviorSubject } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  CustomNotificationId,
  MmobileNotification,
  Notification
} from '../../models/notification-pg';
import {
  fromEntriesArr,
  getDate,
  isEmpty,
  isInTheFuture
} from '../../utils/utils';
import { AuthMirrorService } from '../core/auth/auth-mirror.service';
import { MMobileHelperService } from '../core/mmobile-helper.service';
import { NavigationHelper } from '../core/navigation-helper.service';
import { SecureStoreService } from '../core/storage/secure.service';

type NotificationMap = { [key: string]: Notification };

interface NotificationCustomRequest {
  id: CustomNotificationId;
  interpolationParams?: { [key: string]: string };
}
@Injectable({
  providedIn: 'root'
})
export class NotificationHelperService {
  private notificationsSource = new BehaviorSubject<NotificationMap>({});
  private localStorageNotifications: NotificationMap = {};

  constructor(
    private secure: SecureStoreService,
    private mmobileHelper: MMobileHelperService,
    private authMirror: AuthMirrorService,
    private nav: NavigationHelper,
    private ctx: ContextService
  ) {}

  // Unfiltered notifications, not taking into account maxViews, daysUntilNextView...
  get notificationMap$() {
    return this.notificationsSource;
  }

  get filteredNotifications$() {
    return this.notificationsSource.asObservable().pipe(
      map((notifications: NotificationMap) => Object.values(notifications)),
      map(notifications =>
        notifications
          .filter(n => this.areMaxViewsNotExceeded(n))
          .filter(n => this.hasNextDayToShowArrived(n))
          .filter(n => !this.isDontShowUntilNextSessionEnabled(n))
          .filter(({ channel }) => this.filterByChannel(channel))
      )
    );
  }

  action(id: string) {
    const notification: Notification = this.notificationsSource.getValue()[id];
    if (!notification) {
      return;
    }
    if (!notification.remainOpenOnAction) {
      this.close(id);
    }

    const { url } = notification;

    this.nav.toNewWithoutState(url);
  }

  close(id: string) {
    const notificationMap = this.notificationsSource.getValue();
    if (!notificationMap[id]) {
      return;
    }
    delete notificationMap[id];
    this.updateMemory(notificationMap);
  }

  showMeLater(id: string) {
    const notification: Notification = this.notificationsSource.getValue()[id];
    if (!notification) {
      return;
    }
    const updatedNotification: Notification = {
      ...notification,
      btnShowLaterPressedDate: getDate()
    };

    this.updateNotification(updatedNotification);
  }

  /**
   * @description close() would be a better option than this method.
   * But there are some services that need to proccess the request (I.e capture documents) .
   *
   * Hence this option, the service dependable of showing the notification might response that the
   * notification still pending. But we dont show the notification until next session to improve UX.
   */
  dontShowUntilNextSession(id: string) {
    const notification: Notification = this.notificationsSource.getValue()[id];
    if (!notification) {
      return;
    }
    const updatedNotification: Notification = {
      ...notification,
      dontShowUntilNextSessionSessionCounter:
        this.authMirror.authService.getState().sessionCounter
    };

    this.updateNotification(updatedNotification);
  }

  dontShowMeAgain(id: string) {
    const notification: Notification = this.notificationsSource.getValue()[id];
    if (!notification) {
      return;
    }
    const updatedNotification: Notification = {
      ...notification,
      maxViews: -1
    };

    this.updateNotification(updatedNotification);
  }

  private updateNotification(updatedNotification: Notification) {
    const notifications: NotificationMap = this.notificationsSource.getValue();
    const { id } = updatedNotification;
    const updateNotifications: NotificationMap = {
      ...notifications,
      [id]: updatedNotification
    };

    this.updateMemory(updateNotifications);
    this.setLocalStorageNotification(updatedNotification);
  }

  clear() {
    this.clearLocalAndMemory();
  }

  async updateGenericNotifications() {
    const notifications: Notification[] = await Promise.all(
      this.getMMobileGenericNotifications().map(mmobileNotification =>
        this.incrementViews(mmobileNotification)
      )
    );

    const notificationMap: NotificationMap = notifications
      .map(n => [n.id, n])
      .reduce(fromEntriesArr, {});

    this.updateMemory(notificationMap);
    Object.values(notificationMap).forEach(n =>
      this.secure.saveNotification(n.id, n)
    );
  }

  async updateCustomNotification({
    id,
    interpolationParams
  }: NotificationCustomRequest) {
    if (!this.isACustomNotification(id)) {
      return;
    }

    const mmobileNotification = this.getMMobileCustomNotification(id);

    // Notification deleted from mmobile, then dont show custom notification
    if (!mmobileNotification) {
      return;
    }
    const notificationWithInterpolation = {
      ...mmobileNotification,
      interpolationParams
    };
    const updatedNotification = await this.incrementViews(
      notificationWithInterpolation
    );

    this.updateNotification(updatedNotification);
  }

  private async incrementViews(
    mmobileNotification: MmobileNotification
  ): Promise<Notification> {
    const { id } = mmobileNotification;
    const localStorageNotification =
      (await this.getLocalStorageNotification(id)) || ({} as Notification);
    let outdatedViews = localStorageNotification.views;
    const views =
      this.hasNextDayToShowArrived(localStorageNotification) &&
      this.areMaxViewsNotExceeded(localStorageNotification)
        ? ++outdatedViews || 1
        : outdatedViews;

    return {
      ...localStorageNotification,
      ...mmobileNotification,
      views
    };
  }

  private async getLocalStorageNotification(id: string) {
    if (isEmpty(this.localStorageNotifications[id])) {
      this.localStorageNotifications[id] = await this.secure.getNotification(
        id
      );
    }

    return this.localStorageNotifications[id];
  }

  private setLocalStorageNotification(notification: Notification) {
    const { id } = notification;
    this.localStorageNotifications[id] = notification;
    this.secure.saveNotification(id, notification);
  }

  private getMMobileCustomNotification(
    id: CustomNotificationId
  ): MmobileNotification {
    if (this.authMirror.authService.checkUserIsParticular()) {
      return (
        this.mmobileHelper.getCustomConfigData<MmobileNotification[]>(
          'CUSTOM_NOTIFICATIONS'
        ) || []
      )
      .filter(({ channel }) => this.filterByChannel(channel))
      .find(({ id: notificationId }) => notificationId === id);
    } else {
      return (
        this.mmobileHelper.getCustomConfigData<MmobileNotification[]>(
          'CUSTOM_NOTIFICATIONS_BUSINESSES'
        ) || []
      )
      .filter(({ channel }) => this.filterByChannel(channel))
      .find(({ id: notificationId }) => notificationId === id);
    }
  }

  private getMMobileGenericNotifications() {
    if (this.authMirror.authService.checkUserIsParticular()) {
      return (
        this.mmobileHelper.getCustomConfigData<MmobileNotification[]>(
          'GENERIC_NOTIFICATIONS'
        ) || []
        .filter(({ channel }) => this.filterByChannel(channel))
      );
    } else {
      return (
        this.mmobileHelper.getCustomConfigData<MmobileNotification[]>(
          'GENERIC_NOTIFICATIONS_BUSINESSES'
        ) || []
        .filter(({ channel }) => this.filterByChannel(channel))
      );
    }
  }

  private updateMemory(notifications: NotificationMap) {
    this.notificationsSource.next(notifications);
  }

  private clearLocalAndMemory() {
    this.clearLocal();
    this.clearMemory();
  }

  private clearLocal() {
    Object.keys(this.localStorageNotifications).forEach(id =>
      this.secure.deleteNotification(id)
    );
  }

  private clearMemory() {
    this.notificationsSource.next({});
    this.localStorageNotifications = {};
  }

  private areMaxViewsNotExceeded({ maxViews, views }: Notification) {
    if (isEmpty(maxViews)) {
      return true;
    }
    return views <= maxViews;
  }

  private hasNextDayToShowArrived({
    btnShowLaterPressedDate,
    daysUntilNextView
  }: Notification) {
    if (isEmpty(daysUntilNextView) || isEmpty(btnShowLaterPressedDate)) {
      return true;
    }
    const nextDayToShow = moment(btnShowLaterPressedDate).add(
      daysUntilNextView,
      'days'
    );
    return !isInTheFuture(nextDayToShow);
  }

  private isDontShowUntilNextSessionEnabled({
    dontShowUntilNextSessionSessionCounter
  }: Notification) {
    if (isEmpty(dontShowUntilNextSessionSessionCounter)) {
      return false;
    }
    const currentSession =
      this.authMirror.authService.getState().sessionCounter;
    return dontShowUntilNextSessionSessionCounter === currentSession;
  }

  private isACustomNotification(id: string) {
    return !!CustomNotificationId[id];
  }

  private filterByChannel(channel?: 'APP' | 'WEB') {
    if (!!channel) {
      switch (channel) {
        case 'APP': return this.ctx.isApp;
        case 'WEB': return !this.ctx.isApp;
        default: return false;
      }
    }
    return true;
  }
}
